import React, { useCallback, useEffect, useState } from "react";
import {
  Table,
  Card,
  Typography,
  Space,
  Button,
  Input,
  Select,
  Modal,
  message,
  Spin,
  Switch,
  Form,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  NotificationOutlined,
} from "@ant-design/icons";
import { supabase } from "../lib/supabase";
import { AdminNavBar } from "../components/AdminNavBar";
import { trackFeatureUsage } from "../lib/posthog";
import { useUser } from "../hooks/useUser";
import type { AnnouncementBanner, AnnouncementBannerStyle } from "../types/announcementBanner";

const { Title } = Typography;
const { TextArea } = Input;

const STYLE_OPTIONS = [
  { value: "error", label: "Error (red)" },
  { value: "info", label: "Info (blue)" },
  { value: "success", label: "Success (green)" },
  { value: "warning", label: "Warning (orange)" },
] as const;

const DEFAULT_STYLE: AnnouncementBannerStyle = "info";

const AnnouncementBannerAdmin: React.FC = () => {
  const { user } = useUser();
  const [banners, setBanners] = useState<AnnouncementBanner[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [form] = Form.useForm<{ title: string; subject: string; style: AnnouncementBannerStyle }>();

  const fetchBanners = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("announcement_banners")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      message.error("Failed to load banners: " + error.message);
      setBanners([]);
    } else {
      setBanners((data as AnnouncementBanner[]) ?? []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  const openCreate = () => {
    setEditingId(null);
    form.setFieldsValue({ title: "", subject: "", style: DEFAULT_STYLE });
    setModalOpen(true);
  };

  const openEdit = (row: AnnouncementBanner) => {
    setEditingId(row.id);
    form.setFieldsValue({
      title: row.title,
      subject: row.subject,
      style: row.style,
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    const values = await form.validateFields().catch(() => null);
    if (!values) return;

    setSubmitLoading(true);
    try {
      if (editingId) {
        const { error } = await supabase
          .from("announcement_banners")
          .update({
            title: values.title,
            subject: values.subject ?? "",
            style: values.style ?? DEFAULT_STYLE,
          })
          .eq("id", editingId);

        if (error) throw error;
        message.success("Banner updated");
      } else {
        const { error } = await supabase.from("announcement_banners").insert({
          title: values.title,
          subject: values.subject ?? "",
          style: values.style ?? DEFAULT_STYLE,
          is_live: false,
          created_by: user?.id ?? null,
        });

        if (error) throw error;
        message.success("Banner created");
      }
      setModalOpen(false);
      fetchBanners();
      trackFeatureUsage("admin_announcement_banner_save");
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = (row: AnnouncementBanner) => {
    Modal.confirm({
      title: "Delete banner",
      content: `Delete "${row.title}"?`,
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        const { error } = await supabase
          .from("announcement_banners")
          .delete()
          .eq("id", row.id);
        if (error) {
          message.error("Failed to delete: " + error.message);
          return;
        }
        message.success("Banner deleted");
        fetchBanners();
        trackFeatureUsage("admin_announcement_banner_delete");
      },
    });
  };

  const handleSetLive = async (row: AnnouncementBanner, checked: boolean) => {
    const { error } = await supabase
      .from("announcement_banners")
      .update({ is_live: checked })
      .eq("id", row.id);

    if (error) {
      message.error("Failed to update: " + error.message);
      return;
    }
    message.success(checked ? "Banner is now live" : "Banner taken offline");
    fetchBanners();
    // Refresh the displayed banners in Layout so admin sees the change instantly
    window.dispatchEvent(new CustomEvent("announcement-banners-refresh"));
    trackFeatureUsage("admin_announcement_banner_set_live", { is_live: checked });
  };

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: 180,
      ellipsis: true,
      render: (t: string) => (t || "—"),
    },
    {
      title: "Subject",
      dataIndex: "subject",
      key: "subject",
      width: 200,
      ellipsis: true,
      render: (s: string) => (s || "—"),
    },
    {
      title: "Style",
      dataIndex: "style",
      key: "style",
      width: 100,
      render: (s: AnnouncementBannerStyle) => (
        <span style={{ textTransform: "capitalize" }}>{s}</span>
      ),
    },
    {
      title: "Set live",
      dataIndex: "is_live",
      key: "is_live",
      width: 100,
      render: (_: boolean, row: AnnouncementBanner) => (
        <Switch
          checked={row.is_live}
          onChange={(c) => handleSetLive(row, c)}
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_: unknown, row: AnnouncementBanner) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEdit(row)}
          >
            Edit
          </Button>
          <Button
            type="link"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(row)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <AdminNavBar />
      <Title level={2}>
        <NotificationOutlined style={{ marginRight: 8 }} />
        Announcement banner
      </Title>

      <Card>
        <Space style={{ marginBottom: 16 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Add banner
          </Button>
          <Button icon={<ReloadOutlined />} onClick={fetchBanners}>
            Refresh
          </Button>
        </Space>

        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={banners}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 700 }}
        />
      </Card>

      <Modal
        title={editingId ? "Edit banner" : "Add banner"}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        confirmLoading={submitLoading}
        destroyOnClose
        width={520}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: "Required" }]}
          >
            <Input placeholder="e.g. Maintenance notice" />
          </Form.Item>
          <Form.Item name="subject" label="Subject / body">
            <TextArea rows={4} placeholder="Full message shown in the banner" />
          </Form.Item>
          <Form.Item
            name="style"
            label="Style"
            rules={[{ required: true }]}
            initialValue={DEFAULT_STYLE}
          >
            <Select options={STYLE_OPTIONS} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AnnouncementBannerAdmin;
